import type { PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { GetMyTasksPayload } from '@/domains/tasks/features/myTaskList/slice/types/MyTasksState';
import type { EDSAdapterServiceNormalizeConfiguration } from '@/features/eds/services/types/EDSAdapterServiceInjectionConfig';
import type { EDSPaginatedListResponse } from '@/features/eds/types/api/responses/EDSPaginatedListResponse';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { myTasksFilters } from '@/domains/tasks/constants/myTasksFilters';
import { TaskListItemSlots } from '@/domains/tasks/features/myTaskList/enums/TaskListItemSlots';
import { selectTaskListPagination } from '@/domains/tasks/features/myTaskList/slice/taskListSelectors';
import {
  getTaskList,
  getTaskListSuccess,
  incrementTaskListPage,
  setTaskListCounters,
} from '@/domains/tasks/features/myTaskList/slice/taskListSlice';
import { selectUser } from '@/domains/user/store/selectors';
import { updateCustomInboxCounter } from '@/domains/user/store/userSlice';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import RecordService from '@/services/RecordService/RecordService';

function* getTaskListCountersSaga() {
  yield delay(100);
  const user = yield select(selectUser);
  const userId = user?.personId;

  try {
    const requestsBody = [
      [],
      [
        {
          propertyName: 'AssigneeId',
          values: [userId],
        },
        {
          propertyName: myTasksFilters.propertyName,
          values: myTasksFilters.values,
        },
      ],
    ];

    const [
      {
        data: {
          pagination: { numberOfItems: allTasks },
        },
      },
      {
        data: {
          pagination: { numberOfItems: myTasks },
        },
      },
    ] = yield all(
      requestsBody.map((requestBody) =>
        call(
          HttpService.post,
          `/filter/${EdsEntityCodes.Tasks}?${QueryParamsService.constructSearchParamsFromRecord({ page: 1, perPage: 0, section: EdsDefinitionSections.MyTasks })}`,
          requestBody,
          { baseURL: import.meta.env.VITE_API_URL_EDS },
        ),
      ),
    );

    yield put(setTaskListCounters({ allTasks: allTasks || 0, myTasks: myTasks || 0 }));
  } catch (error) {
    console.error(error);
  }
}

function* getTaskListSaga({ payload }: PayloadAction<GetMyTasksPayload>) {
  yield delay(300);

  try {
    const pagination = yield select(selectTaskListPagination);
    const requestUrl = `/filter/${EdsEntityCodes.Tasks}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, sortBy: payload?.sorting?.propertyName, sortDirection: payload?.sorting?.sortDirection, includes: `${EdsEntityCodes.HelperTags}` })}`;

    const { data }: HttpServiceResponse<EDSPaginatedListResponse> = yield call(
      HttpService.post,
      requestUrl,
      payload.filters,
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizeConfig: Partial<EDSAdapterServiceNormalizeConfiguration> = { includeId: true };

    if (payload.section) normalizeConfig.section = payload.section;

    const normalizedResponse: NormalizedV2Response[] = yield call(
      EdsAdapterService.normalize,
      data.listings,
      normalizeConfig,
    );

    const tasksWithoutAvailableHelperTags = normalizedResponse.map((normalizedTask) => {
      return {
        ...normalizedTask,
        entities: {
          ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedTask.entities),
        },
      };
    });

    EdsDebugService.logResponse(EdsEntityCodes.Tasks, {
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse.length
        ? EdsAdapterService.generateSlotRecord<TaskListItemSlots>(normalizedResponse[0])
        : {},
      section: payload?.section,
    });

    if (payload?.location && payload?.customInboxId)
      yield put(
        updateCustomInboxCounter({
          id: payload.customInboxId,
          location: payload.location,
          counter: data.pagination.numberOfItems,
        }),
      );

    yield put(
      getTaskListSuccess({
        taskList: tasksWithoutAvailableHelperTags,
        pagination: {
          page: data.pagination.page,
          perPage: data.pagination.perPage,
          isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
        },
      }),
    );
  } catch (error) {
    console.error(error);
  }
}

export function* taskListWatcher() {
  yield takeLatest([getTaskList.type, incrementTaskListPage.type], getTaskListSaga);
  yield takeLatest([getTaskList.type], getTaskListCountersSaga);
}
